// 9 Oct: the end-of-battle details form, shared by the details step between the podium and the result
// (screens/Details.tsx) and the result page, which opens it inline to change saved details or, for a player who skipped
// the step, as an offer. The fields are those of 6 Oct (first and last name, country, subject, university or medical
// school, year, what they are preparing for), with the prize draw's opt-in when the battle has draw prizes. Saving is
// PUT /me/profile. Here: the form itself, the draw's claim window and the save.
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import type { BattleTerms, MeResponse, StateSnapshot } from '@shared/types';
import { PREPARING_FOR, SUBJECTS } from '@shared/types';
import { api, ApiError, serverNow } from '@/lib/api';
import { Button } from '@/components/Button';
import { GEORGIA_MED_SCHOOLS, MED_SCHOOLS, YEARS } from '@/data/medSchools';
import { GEORGIA, UK, countriesFor, phoneCountry } from '@/data/countries';
import { FINISH, type SavedKind } from './finish';
import { activePrize, claimClosesAt, closedAtLabel, prizePhrase, termsPath } from './prize';
import { useRerenderAt } from './time';

/** What the form holds. The draw box starts unticked: the prize terms say players choose to enter. */
export interface DetailsValues { firstName: string; lastName: string; country: string; medSchool: string; subject: string; year: string; prep: string; drawOptIn: boolean }
export const EMPTY_DETAILS: DetailsValues = { firstName: '', lastName: '', country: '', medSchool: '', subject: '', year: '', prep: '', drawOptIn: false };

/** Saved details (GET /me, asked with the session only) laid over the form, so changing or confirming them is a tap; the draw box stays as it was. */
export function withSaved(v: DetailsValues, d: MeResponse['details']): DetailsValues {
  return d ? { ...v, ...d, prep: d.prep ?? '' } : v;
}

/** The draw a battle offers and whether its claim window has closed (claimHours after the end; the server has the last word when the form is saved). */
export interface DrawWindow {
  prize?: ReturnType<typeof activePrize>;
  /** The battle has draw prizes, so the form offers the opt-in. */
  draw: boolean;
  closed: boolean;
  closesAt?: number;
  /** The server said the draw had closed. */
  markClosed: () => void;
}
export function useDrawWindow(state: Pick<StateSnapshot, 'code' | 'prize'> | undefined): DrawWindow {
  const prize = activePrize(state?.prize);
  const draw = Boolean(prize && prize.drawPrizes > 0);
  const code = state?.code;
  const [terms, setTerms] = useState<BattleTerms>(); const [serverClosed, setServerClosed] = useState(false);
  useEffect(() => {
    if (!draw || !code) return;
    let live = true;
    api<BattleTerms>(`/battles/${code}/terms`).then((t) => { if (live && t && typeof t === 'object') setTerms(t); }).catch(() => {});
    return () => { live = false; };
  }, [draw, code]);
  const closesAt = draw ? claimClosesAt(terms) : undefined;
  useRerenderAt(closesAt);
  const closed = draw && (serverClosed || (closesAt !== undefined && serverNow() > closesAt));
  return { prize, draw, closed, closesAt, markClosed: () => setServerClosed(true) };
}

/**
 * Saves the form with the token given (this device's session for the player, or a result link's token with the email
 * they joined with). The draw is entered only while it is open and the box is ticked. Says what was saved.
 */
export async function saveDetails(token: string, values: DetailsValues, opts: { draw: DrawWindow; email?: string }): Promise<Exclude<SavedKind, 'no' | 'before'>> {
  const entering = opts.draw.draw && !opts.draw.closed && values.drawOptIn;
  const body = { ...values, drawOptIn: entering, ...(opts.email !== undefined ? { email: opts.email.trim() } : {}) };
  const r = await api<{ saved?: boolean; drawClosed?: boolean }>('/me/profile', { method: 'PUT', token, body: JSON.stringify(body) });
  if (r?.drawClosed) opts.draw.markClosed();
  return entering ? (r?.drawClosed ? 'closed' : 'draw') : 'plain';
}

const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/\.?$/, '.') : s);
/** A refused or failed save, in words for the form. */
export const detailsError = (e: unknown): string => (e instanceof ApiError ? sentence(e.message) : FINISH.network);

const field = 'flex flex-col gap-1.5';
const label = 'text-sm font-semibold text-ink-2';

/**
 * The form. `head` goes at its top (the result page's heading and message); the details step puts its heading above the
 * form and names it with `labelledBy`. A result link opened on a device that did not join asks for the email they joined
 * with (`needEmail`). Medicine in the UK picks from the medical schools; anywhere else, or any other subject, the
 * university is typed.
 */
export function DetailsForm({ values, onChange, labelledBy, head, needEmail = false, email = '', onEmail, draw, code, err, busy, submitLabel, onSubmit, onCancel }: {
  values: DetailsValues; onChange: (v: DetailsValues) => void; labelledBy: string; head?: ReactNode;
  needEmail?: boolean; email?: string; onEmail?: (v: string) => void;
  draw: DrawWindow; code: string; err?: string; busy: boolean; submitLabel: string;
  onSubmit: () => void; onCancel?: () => void;
}) {
  const set = (patch: Partial<DetailsValues>) => onChange({ ...values, ...patch });
  // 16 Oct: the country this phone is most likely in heads the list (its time zone, else its language); nothing is chosen for them.
  const countries = useMemo(() => countriesFor(phoneCountry()), []);
  // A list of medical schools where we have one (UK; Georgia from 14 Oct), for medicine students; anything else is typed.
  const schools: readonly string[] | undefined = values.subject !== 'Medicine' ? undefined : values.country === UK ? MED_SCHOOLS : values.country === GEORGIA ? GEORGIA_MED_SCHOOLS : undefined;
  const ukList = Boolean(schools);
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(); };
  const { prize, closed, closesAt } = draw;
  return (
    <form onSubmit={submit} aria-labelledby={labelledBy} data-part="details-form" className="flex flex-col gap-4 rounded-card bg-surface p-5 shadow-card">
      {head}
      {needEmail && (
        <div className={field}>
          <label className={field}><span className={label}>{FINISH.email}</span>
            <input id="email" type="email" className="rd-field" autoComplete="email" inputMode="email" maxLength={254} aria-describedby="email-why"
              value={email} onChange={(e) => onEmail?.(e.target.value)} required /></label>
          <p id="email-why" className="text-sm text-ink-2">{FINISH.emailWhy}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className={field}><span className={label}>{FINISH.firstName}</span>
          <input id="firstName" className="rd-field" autoComplete="given-name" maxLength={40} value={values.firstName} onChange={(e) => set({ firstName: e.target.value })} required /></label>
        <label className={field}><span className={label}>{FINISH.lastName}</span>
          <input id="lastName" className="rd-field" autoComplete="family-name" maxLength={40} value={values.lastName} onChange={(e) => set({ lastName: e.target.value })} required /></label>
      </div>
      <label className={field}><span className={label}>{FINISH.country}</span>
        <select id="country" className="rd-field" value={values.country} onChange={(e) => set({ country: e.target.value })} required>
          <option value="">{FINISH.choose}</option>{countries.map((s) => <option key={s}>{s}</option>)}
        </select></label>
      <label className={field}><span className={label}>{FINISH.subject}</span>
        <select id="subject" className="rd-field" value={values.subject} onChange={(e) => set({ subject: e.target.value })} required>
          <option value="">{FINISH.choose}</option>{SUBJECTS.map((s) => <option key={s}>{s}</option>)}
        </select></label>
      <label className={field}><span className={label}>{ukList ? FINISH.medSchool : FINISH.university}</span>
        {ukList
          ? <select id="medSchool" className="rd-field" value={schools!.includes(values.medSchool) ? values.medSchool : ''} onChange={(e) => set({ medSchool: e.target.value })} required>
              <option value="">{FINISH.choose}</option>{schools!.map((s) => <option key={s}>{s}</option>)}
            </select>
          : <input id="medSchool" className="rd-field" autoComplete="organization" maxLength={80} value={values.medSchool} onChange={(e) => set({ medSchool: e.target.value })} required />}
      </label>
      <label className={field}><span className={label}>{FINISH.year}</span>
        <select id="year" className="rd-field" value={values.year} onChange={(e) => set({ year: e.target.value })} required>
          <option value="">{FINISH.choose}</option>{YEARS.map((s) => <option key={s}>{s}</option>)}
        </select></label>
      <label className={field}><span className={label}>{FINISH.prep} <span className="font-normal text-ink-3">{FINISH.optional}</span></span>
        <select id="prep" className="rd-field" value={values.prep} onChange={(e) => set({ prep: e.target.value })}>
          <option value="">{FINISH.choose}</option>{PREPARING_FOR.map((s) => <option key={s}>{s}</option>)}
        </select></label>

      {draw.draw && prize && (
        <div className="flex flex-col gap-1.5 rounded-tile bg-surface-2 px-3.5 py-3">
          <label className={`flex min-h-11 items-start gap-3 text-[15px] leading-snug ${closed ? 'text-ink-3' : 'text-ink'}`}>
            <input id="drawOptIn" type="checkbox" className="mt-0.5 size-5 shrink-0 accent-brand disabled:opacity-60" checked={!closed && values.drawOptIn} disabled={closed}
              aria-describedby={closed ? 'draw-terms draw-closed' : 'draw-terms'} onChange={(e) => set({ drawOptIn: e.target.checked })} />
            <span>{FINISH.draw(prizePhrase(prize.prize))}</span>
          </label>
          <p id="draw-terms" className="pl-8 text-sm text-ink-2">
            {FINISH.agreeBefore}<a href={termsPath(code)} target="_blank" rel="noopener" className="py-3 font-semibold text-iv-strong underline underline-offset-2">{FINISH.agreeLink}</a>{FINISH.agreeAfter}
          </p>
          {closed && <p id="draw-closed" role="status" className="rounded-tile bg-[var(--amber-soft)] px-3 py-2 text-sm text-[var(--amber-strong)]">{FINISH.closed(closesAt !== undefined ? closedAtLabel(closesAt) : undefined)}</p>}
        </div>
      )}

      {err && <p role="alert" className="rounded-tile bg-wrong-soft px-4 py-3 text-[15px] text-ink">{err}</p>}
      <div className="flex gap-3">
        {onCancel && <Button type="button" variant="quiet" className="w-auto" onClick={onCancel}>{FINISH.cancel}</Button>}
        <Button type="submit" className={onCancel ? 'flex-1' : undefined} disabled={busy} busy={busy}>{busy ? FINISH.saving : submitLabel}</Button>
      </div>
    </form>
  );
}
